import { ReactComponent as LeftArrow } from 'assets/icons/arrow-left-icon.svg';
import { ReactComponent as RightArrow } from 'assets/icons/arrow-right-icon.svg';
import davLogo from 'assets/icons/davidson-logo.svg';
import { ReactComponent as LbmIcon } from 'assets/icons/lbm-logo.svg';
import { Lot, Positions, Video } from 'constants/index';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import { setIntroLotId, setLot, setStepToBadge } from 'redux/slices/game';
import { selectCurrentVideoOrder, setCurrentVideoOrder } from 'redux/slices/introductionLot';
import { setNotificationMessage, setNotificationType } from 'redux/slices/notification';
import { setType } from 'redux/slices/popup';
import { selectEmail } from 'redux/slices/user';
import { addLotToUser, getIntroLot } from 'services/lot/lotService';
import { getCurrentUserEosId, updateDutlerStatus } from 'services/user/userServices';
import { isApiMediaUrl, resolveMediaUrl } from 'utils/media/resolveMediaUrl';
import { Button } from 'views/buttons/base';
import './introGame.scss';

// Empty WebVTT track shipped with the front, used when a video has no captions (see nginx.conf)
const EMPTY_CAPTIONS_URL = '/captions/empty.vtt';

export function IntroGame() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['game']);

  const [videos, setVideos] = useState<Video[]>([]);
  const [isVideoFinished, setIsVideoFinished] = useState(false);
  const [displayEndButton, setDisplayEndButton] = useState(false);
  const [introLotData, setIntroLotData] = useState<Lot | null>(null);
  const [timer, setTimer] = useState(0);
  const currentVideoTotalTime = useRef(0);
  const timeElapsedOnVideos = useRef<Array<[number, number]>>([]);
  const isDav = process.env.REACT_APP_IS_DAV === 'true';

  let videoOrderOnDisplay: number = useAppSelector(selectCurrentVideoOrder);
  const userEmail = useAppSelector(selectEmail);

  const getIntroductionLot = async () => {
    await getIntroLot().then((values) => {
      if (values) {
        setIntroLotData(values);
        dispatch(setLot(values));
        dispatch(setIntroLotId(values._id));
        setVideos(sortByOrder(values.videos));
        setDisplayEndButton(values.videos.length === 1);
      }
    });
  };

  // Videos are plain media files (mp4) served by the api (/media/videos/<file>.mp4, resolved
  // against REACT_APP_API_URL) or, for a few external ones, by an absolute url.
  const sortByOrder = (videoList: Video[]): Video[] => {
    return [...videoList].sort((a, b) => a.videoOrder - b.videoOrder);
  };

  const backHome = () => {
    dispatch(setType('validateBackToHome'));
  };

  const onVideoLoaded = (event: SyntheticEvent<HTMLVideoElement>) => {
    // duration is NaN before the metadata and Infinity when the browser cannot tell it,
    // neither survives JSON.stringify in the shape [seconds spent, duration] the api expects
    const { duration } = event.currentTarget;
    currentVideoTotalTime.current = Number.isFinite(duration) ? Math.round(duration) : 0;
  };

  const onVideoEnded = () => {
    setIsVideoFinished(true);
  };

  // A missing or unreadable file leaves the native player blank: tell the player and log the
  // resolved address so that ops can find which file is missing on the volume.
  const onVideoError = (event: SyntheticEvent<HTMLVideoElement>) => {
    console.error('Introduction video could not be loaded:', event.currentTarget.currentSrc);
    dispatch(setNotificationType('error'));
    dispatch(setNotificationMessage(t('videoLoadError')));
  };

  useEffect(() => {
    setIsVideoFinished(false);
    // Otherwise a video that fails to load reports the duration of the previous one
    currentVideoTotalTime.current = 0;
    if (videos.length == 1) {
      setDisplayEndButton(true);
    } else {
      setDisplayEndButton(videoOrderOnDisplay === videos.length - 1);
    }
  }, [videoOrderOnDisplay, videos.length]);

  const updateTimeElapsed = () => {
    timeElapsedOnVideos.current.push([timer, currentVideoTotalTime.current]);
  };

  const nextVideo = () => {
    updateTimeElapsed();
    setTimer(0);
    dispatch(setCurrentVideoOrder(videoOrderOnDisplay + 1));
  };

  const previousVideo = () => {
    dispatch(setCurrentVideoOrder(videoOrderOnDisplay - 1));
  };

  const finishGame = async () => {
    updateTimeElapsed();
    if (introLotData && introLotData._id) {
      const submit = await addLotToUser(introLotData._id, userEmail, 0, 0, 0, timeElapsedOnVideos.current);
      if (submit) {
        try {
          const eosId = await getCurrentUserEosId();
          if (eosId) {
            await updateDutlerStatus(eosId, introLotData._id);
          } else {
            console.warn('No eosId found for this user, Dutler update skipped');
          }
        } catch (dutlerError) {
          console.error('Error during Dutler update:', dutlerError);
        }

        dispatch(setStepToBadge());
      }
    }
  };

  useEffect(() => {
    getIntroductionLot();
    const interval = setInterval(() => setTimer((timer) => timer + 1), 1000);
    return () => {
      setIsVideoFinished(false);
      setDisplayEndButton(false);
      setIntroLotData(null);
      setVideos([]);
      dispatch(setCurrentVideoOrder(0));
      clearInterval(interval);
    };
  }, []);

  const currentVideo = videos[videoOrderOnDisplay];

  return (
    <>
      <div className="introGame">
        <NavLink to="/">
          <LbmIcon className="lbm-logo" />
        </NavLink>
        {isDav && <img className="davLogo" src={davLogo} alt="davidson logo" />}
        <Button
          label="backHome"
          type="button"
          classType="secondary-button-add-card home-button"
          translation="game"
          onClick={backHome}
          Icon={{
            Svg: LeftArrow,
            position: Positions.START
          }}
        />
        <div className="introGame__video-container">
          {currentVideo && (
            <video
              key={currentVideo._id ?? videoOrderOnDisplay}
              className="introGame__video"
              src={resolveMediaUrl(currentVideo.url)}
              controls
              autoPlay
              controlsList="nodownload"
              // CORS mode is needed for a cross-origin <track>, but it would block an external
              // video whose host sends no CORS header: only ask for it with api-hosted media.
              crossOrigin={
                isApiMediaUrl(currentVideo.url) || isApiMediaUrl(currentVideo.captionsUrl) ? 'anonymous' : undefined
              }
              onLoadedMetadata={onVideoLoaded}
              onEnded={onVideoEnded}
              onError={onVideoError}
            >
              <track
                kind="captions"
                src={resolveMediaUrl(currentVideo.captionsUrl) ?? EMPTY_CAPTIONS_URL}
                srcLang="fr"
                label="Français"
              />
            </video>
          )}
        </div>
        {videoOrderOnDisplay > 0 && (
          <Button
            label={'previous'}
            type="button"
            classType="secondary-button-add-card introGame__previous-button"
            translation="game"
            onClick={() => {
              previousVideo();
            }}
            Icon={{
              Svg: LeftArrow,
              position: Positions.START
            }}
          />
        )}
        <Button
          label={displayEndButton ? 'end' : 'next'}
          type="button"
          classType={
            isVideoFinished ? 'primary-button-add-card introGame__next-button' : 'off-button-lg introGame__next-button'
          }
          translation="game"
          onClick={() => {
            displayEndButton ? finishGame() : nextVideo();
          }}
          Icon={{
            Svg: RightArrow,
            position: Positions.END
          }}
        />
      </div>
    </>
  );
}
